import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import Radio from 'component/Layout/Form/Radio';

import classNames from 'classnames';

import { useGuestRating } from 'hooks/form/filter';
import { useAccommodationMeta } from 'hooks/form/meta';
import { useChangedFilterListForSection } from 'hooks/form/useChangedFilterList';
import { QaIdentifier } from 'hooks/useQa';
import { getRatingConfig } from 'hooks/useRatingConfig';

import { SearchParam } from 'types/Search/params';

import styles from '../Filter.module.scss';
import Segment from '../Segment';
import {
    MetaValue,
    useDisabledStatus,
} from '../Segment/__hooks/useDisabledStatus';
import FilterLabel from '../Segment/FilterLabel';
import { SectionName } from '../types';

import { getGuestRatingLabel } from './__helper/guestRatingLabel';

const GuestRating = ({ disableWhen = MetaValue.NoResult }) => {
    const { t } = useTranslation();
    const [accommodationMeta] = useAccommodationMeta();
    const [guestRating, setGuestRating] = useGuestRating();
    const getDisabledStatus = useDisabledStatus(disableWhen);
    const ratingFilterConfig = getRatingConfig();
    const ratingFilterRadioList = ratingFilterConfig?.filterThresholds;
    const { onClear, onSelect, isFilterChanged } =
        useChangedFilterListForSection(SectionName.GuestRating, () => {
            const current = ratingFilterRadioList.find(
                (rating) => rating.threshold === guestRating
            );
            return !!current?.threshold
                ? `${SectionName.GuestRating}${current.threshold}`
                : undefined;
        });
    const onSelect_ = useCallback(
        (selectedRatingFilter: number) => {
            onSelect(`${SectionName.GuestRating}${selectedRatingFilter}`);
            setGuestRating(selectedRatingFilter);
        },
        [setGuestRating, onSelect]
    );

    const isSelected = useCallback(
        (value: number | undefined) => {
            return value === guestRating;
        },
        [guestRating]
    );

    const headline = t('TRANS-guest-rating[i]', {
        postProcess: 'interval',
        count: 2,
    });

    return (
        <Segment
            headline={{
                text: headline,
            }}
            withDeleteLink
            qaIdentifier={QaIdentifier.Search_Filter_Section_GuestRating}
            onDelete={() => {
                onClear();
                setGuestRating(undefined);
            }}
            kiFilterGroupData={{
                refKey: SectionName.GuestRating,
                label: headline,
                multi: false,
                minLogic: true,
            }}
            filterList={ratingFilterRadioList.map((rating, index) => {
                const threshold = rating.threshold;
                const guestRatingMeta = accommodationMeta?.rating?.[threshold];
                const isChecked = isSelected(threshold);
                const label = getGuestRatingLabel(
                    ratingFilterRadioList[index].labelKey,
                    threshold,
                    t
                );
                const isDisabled =
                    !isChecked &&
                    guestRatingMeta &&
                    getDisabledStatus(guestRatingMeta);

                return {
                    filterValue: label,
                    content: (
                        <Radio
                            key={threshold}
                            input={{
                                id: `cbRating${threshold}`,
                                value: `${threshold}`,
                                name: 'cbRating',
                            }}
                            isChecked={isChecked}
                            isDisabled={isDisabled}
                            isOnlyDeselectable={
                                isChecked &&
                                guestRatingMeta?.count === 0 &&
                                guestRatingMeta?.hasCount
                            }
                            onChange={() => onSelect_(threshold)}
                            withUXSelection
                            customClass={{
                                Wrapper: classNames({
                                    [styles.Changed]: isFilterChanged(
                                        `${SectionName.GuestRating}${threshold}`
                                    ),
                                }),
                            }}
                        >
                            <FilterLabel
                                labelText={label}
                                {...guestRatingMeta}
                            />
                        </Radio>
                    ),
                    isSelected: isChecked,
                    kiFilterData: {
                        label,
                        isSelectable: !isDisabled,
                        urlKey: SearchParam.Rating,
                        value: threshold.toString() ?? '',
                        ...guestRatingMeta,
                    },
                };
            })}
        />
    );
};

export default GuestRating;
